/**
 * Results Panel
 *
 * The right-side panel in the engaged Command Center layout.
 * Displays rich content cards (assets, locations, health summaries)
 * based on the current conversation context. Replaces inline card
 * rendering in chat bubbles with a dedicated, spacious display area.
 */

import { Button } from "antd";
import {
	ArrowRightOutlined,
	BulbOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import { ChatCard, type ChatCardData } from "./ChatCards";
import "./ResultsPanel.scss";

// Navigation path labels
const NAV_LABELS: Record<string, string> = {
	"/": "Dashboard",
	"/locations": "Locations",
	"/assets": "Assets",
	"/forms": "Vision Capture",
	"/asset-scanner": "Asset Scanner",
	"/predictive-spend": "Predictive Spend",
	"/admin": "Administration",
	"/profile": "My Profile",
};

interface ResultsPanelProps {
	cards: ChatCardData[];
	pendingNavigation: string | null;
	onNavigate: (path: string) => void;
	isLoading: boolean;
}

const ResultsPanel = ({ cards, pendingNavigation, onNavigate, isLoading }: ResultsPanelProps) => {
	const hasCards = cards.length > 0;

	return (
		<div className="results-panel">
			<div className="rp-header">
				<h3 className="rp-title">Insights</h3>
			</div>

			<div className="rp-content">
				{/* Loading state */}
				{isLoading && !hasCards && (
					<div className="rp-loading">
						<div className="rp-loading-dots">
							<span />
							<span />
							<span />
						</div>
						<p className="rp-loading-text">Analyzing your data...</p>
					</div>
				)}

				{/* Cards grid */}
				{hasCards && (
					<div className="rp-cards">
						{cards.map((card, index) => (
							<div key={index} className="rp-card-wrapper">
								<ChatCard data={card} />
							</div>
						))}
					</div>
				)}

				{/* Navigation action */}
				{pendingNavigation && !isLoading && (
					<div className="rp-navigation">
						<Button
							type="primary"
							icon={<ArrowRightOutlined />}
							onClick={() => onNavigate(pendingNavigation)}
							className="rp-nav-btn"
							size="large"
							block
						>
							Open in {NAV_LABELS[pendingNavigation] || "Deep Data"}
						</Button>
					</div>
				)}

				{/* Empty state */}
				{!hasCards && !isLoading && (
					<div className="rp-empty">
						<div className="rp-empty-icon">
							<SearchOutlined />
						</div>
						<p className="rp-empty-title">Results appear here</p>
						<p className="rp-empty-text">
							Ask about your locations, assets, or health scores to see detailed summaries and insights.
						</p>
						<div className="rp-empty-suggestions">
							<p className="rp-empty-hint"><BulbOutlined /> Try asking:</p>
							<ul>
								<li>"Give me an overview of my facilities"</li>
								<li>"What assets need attention?"</li>
								<li>"Show me my locations"</li>
							</ul>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export { ResultsPanel };
